export interface UUID {
    expiresAt: Date;
    UUID: string;
}

/**
 * Handles UUID for ActionAnalytics
 * UUID is stored in session storage
 * UUID is generated on first call of getUUID() method
 * UUID is refreshed on every call of getUUID() method, if it is not expired
 * UUID is generated again new if it is expired
 */
export class ActionAnalyticsUUID {
    private readonly SERP_UUID_IDENTIFIER = 'SERP_UUID' as const;
    private readonly expiration: number;

    constructor(expiration: number) {
        this.expiration = expiration;
    }

    /**
     * gets UUID from session storage and refreshes its expiraton
     * or generates new UUID and sets it to session storage
     */
    public getUUID(): string {
        const sessionUUID = this.getUUIDFromSessionStorage();
        if (sessionUUID) {
            // if UUID is expired, generate new one
            if (new Date() > sessionUUID.expiresAt) {
                return this.generateNewUUID().UUID;
            }
            // if UUID is not expired, refresh its expiration date
            return this.refreshUUID(sessionUUID).UUID;
        }
        return this.generateNewUUID().UUID;
    }

    /**
     * Takes UUID object from session storage and prolongs its expiration date
     */
    private refreshUUID(uuid: UUID): UUID {
        const newUUID: UUID = {
            expiresAt: this.getUUIDExpirationDate(),
            UUID: uuid.UUID,
        };
        this.setUUIDToSessionStorage(newUUID);
        return newUUID;
    }

    /**
     * Tryes to get UUID object from session storage
     */
    private getUUIDFromSessionStorage(): UUID | null {
        const sessionObj = sessionStorage.getItem(this.SERP_UUID_IDENTIFIER);
        if (sessionObj) {
            const uuid = JSON.parse(sessionObj);
            // TODO: add proper validation
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            if (!uuid.expiresAt || !uuid.UUID) {
                console.error('UUID object saved in session storage is not valid');
                return null;
            }
            // TODO: add proper validation
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            return { ...uuid, expiresAt: new Date(uuid.expiresAt) };
        }
        return null;
    }

    /**
     * removes old UUID from session storage
     * generates new UUID and sets it to session storage
     */
    private generateNewUUID(): UUID {
        this.removeOldUUIDFromSessionStorage();

        const newUUID: UUID = {
            expiresAt: this.getUUIDExpirationDate(),
            UUID: this.createUUID(),
        };
        this.setUUIDToSessionStorage(newUUID);
        return newUUID;
    }

    /**
     * removes old UUID from session storage if it exists
     */
    private removeOldUUIDFromSessionStorage(): void {
        const oldSessionUUID = this.getUUIDFromSessionStorage();
        if (!oldSessionUUID) {
            return;
        }
        sessionStorage.removeItem(this.SERP_UUID_IDENTIFIER);
    }

    /**
     * sets UUID to session storage
     */
    private setUUIDToSessionStorage(uuid: UUID): void {
        sessionStorage.setItem(this.SERP_UUID_IDENTIFIER, JSON.stringify(uuid));
    }

    /**
     * returns expiration date for new UUID
     */
    private getUUIDExpirationDate(): Date {
        const expiration = new Date();
        expiration.setMinutes(expiration.getMinutes() + this.expiration);
        return expiration;
    }

    /**
     * generates new UUID
     */
    private createUUID(): string {
        // TODO: maybe consider replacing with Crypto.randomUUID() or uuid package for reasons described here: https://stackoverflow.com/questions/105034/how-to-create-a-guid-uuid-in-javascript
        const matcher = /[xy]/g;
        const replacer = function (c: string) {
            const r = (Math.random() * 16) | 0;
            const v = c === 'x' ? r : (r & 0x3) | 0x8;
            return v.toString(16);
        };
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(matcher, replacer);
    }
}
